'use client'

import {
    createHrEmployee,
    getHrEmployee,
    updateHrEmployee,
} from '@/lib/api/hr/hr-employee-api'
import { getCostCenters, CostCenterItem } from '@/lib/api/financial/cost-center-api'
import { fetchPostalCode, PostalCodeException } from '@/lib/api/fetch-postal-code'
import { getRoles } from '@/lib/api/roles/roles-api'
import { getUsersByCompany } from '@/lib/api/users/users-api'
import { Role } from '@/lib/api/common/interfaces/role.interface'
import { User } from '@/lib/api/common/interfaces/user.interface'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { useEffect, useMemo, useState, useCallback } from 'react'
import {
    MdArrowBack,
    MdBadge,
    MdSave,
} from 'react-icons/md'
import { handleDateInputPaste } from '@/lib/utils/date-input'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

// -- Mask functions --

const formatCpf = (value: string): string => {
    const numbers = value.replace(/\D/g, '')
    if (numbers.length <= 3) return numbers
    if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`
    if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`
    return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`
}

const formatPhone = (value: string): string => {
    const numbers = value.replace(/\D/g, '').slice(0, 11)
    if (numbers.length <= 2) return numbers
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`
    if (numbers.length <= 10) return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`
}

const formatZipCode = (value: string): string => {
    const numbers = value.replace(/\D/g, '')
    if (numbers.length <= 5) return numbers
    return `${numbers.slice(0, 5)}-${numbers.slice(5, 8)}`
}

const formatCurrency = (value: string): string => {
    const numbers = value.replace(/\D/g, '')
    if (!numbers) return ''
    const cents = parseInt(numbers, 10)
    return (cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const parseCurrencyToInt = (value: string): number => {
    const numbers = value.replace(/\D/g, '')
    return parseInt(numbers, 10) || 0
}

// -- Enum labels --

const GENDER_LABELS: Record<string, string> = {
    MALE: 'Masculino',
    FEMALE: 'Feminino',
    OTHER: 'Outro',
}

const MARITAL_STATUS_LABELS: Record<string, string> = {
    SINGLE: 'Solteiro(a)',
    MARRIED: 'Casado(a)',
    DIVORCED: 'Divorciado(a)',
    WIDOWED: 'Viúvo(a)',
    SEPARATED: 'Separado(a)',
    STABLE_UNION: 'União Estável',
}

const RACE_LABELS: Record<string, string> = {
    WHITE: 'Branca',
    BLACK: 'Preta',
    BROWN: 'Parda',
    ASIAN: 'Amarela',
    INDIGENOUS: 'Indígena',
    NOT_DECLARED: 'Não declarada',
}

const CONTRACT_TYPE_LABELS: Record<string, string> = {
    CLT: 'CLT',
    TEMPORARY: 'Temporário',
    INTERMITTENT: 'Intermitente',
    APPRENTICE: 'Aprendiz',
    INTERN: 'Estagiário',
}

const WORK_REGIME_LABELS: Record<string, string> = {
    ONSITE: 'Presencial',
    REMOTE: 'Remoto',
    HYBRID: 'Híbrido',
}

const SALARY_TYPE_LABELS: Record<string, string> = {
    MONTHLY: 'Mensal',
    HOURLY: 'Horista',
    COMMISSION: 'Comissionista',
}

const BANK_ACCOUNT_TYPE_LABELS: Record<string, string> = {
    CHECKING: 'Corrente',
    SAVINGS: 'Poupança',
    SALARY: 'Salário',
}

const PIX_KEY_TYPE_LABELS: Record<string, string> = {
    CPF: 'CPF',
    CNPJ: 'CNPJ',
    EMAIL: 'E-mail',
    PHONE: 'Telefone',
    RANDOM: 'Aleatória',
}

const TABS = [
    { key: 'personal', label: 'Dados Pessoais' },
    { key: 'documents', label: 'Documentos' },
    { key: 'contact', label: 'Contato' },
    { key: 'address', label: 'Endereço' },
    { key: 'bank', label: 'Dados Bancários' },
    { key: 'contract', label: 'Contrato' },
]

const initialFormData = {
    // Dados Pessoais
    name: '',
    cpf: '',
    rg: '',
    rgIssuer: '',
    rgState: '',
    birthDate: '',
    gender: '',
    maritalStatus: '',
    nationality: 'Brasileira',
    birthCity: '',
    birthState: '',
    motherName: '',
    fatherName: '',
    race: '',
    // Documentos
    pis: '',
    ctpsNumber: '',
    ctpsSeries: '',
    ctpsState: '',
    ctpsIssueDate: '',
    voterTitle: '',
    militaryCertificate: '',
    // Contato
    email: '',
    phone: '',
    phone2: '',
    whatsAppNumber: '',
    // Endereço
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    zipCode: '',
    // Dados Bancários
    bankCode: '',
    bankName: '',
    bankAgency: '',
    bankAccount: '',
    bankAccountDigit: '',
    bankAccountType: '',
    pixKey: '',
    pixKeyType: '',
    // Contrato
    /** Filial empregadora. Default: a unidade ativa (resolvida no efeito). */
    companyId: '',
    admissionDate: '',
    contractType: 'CLT',
    workRegime: 'ONSITE',
    weeklyHours: '44',
    jobTitle: '',
    jobFunction: '',
    cboCode: '',
    department: '',
    costCenterId: '',
    salaryReais: '',
    salaryType: 'MONTHLY',
    transportVoucher: false,
    experienceDays: '90',
    userId: '',
}

export default function HrEmployeeFormPage() {
    const { user, accessibleCompanies } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id

    /**
     * Filiais oferecidas como empregadora. A unidade ativa entra na lista mesmo
     * que `accessibleCompanies` ainda não tenha carregado — senão o select
     * nasceria vazio e o cadastro travaria por falta de filial.
     */
    const companyOptions = useMemo(() => {
        const options = accessibleCompanies?.length
            ? accessibleCompanies.map(c => ({ id: c.id, name: c.name }))
            : []
        if (user?.companyId && !options.some(c => c.id === user.companyId)) {
            options.unshift({ id: user.companyId, name: user.company?.name || 'Unidade atual' })
        }
        return options
    }, [accessibleCompanies, user?.companyId])
    const { canCreate, canUpdate } = useResourcePermissions('hr', 'employees')

    const [activeTab, setActiveTab] = useState('personal')
    const [loading, setLoading] = useState(false)
    const [loadingData, setLoadingData] = useState(false)
    const [loadingCep, setLoadingCep] = useState(false)
    const [costCenters, setCostCenters] = useState<CostCenterItem[]>([])
    const [formData, setFormData] = useState(initialFormData)

    // User dropdown / create user states
    const [createSystemUser, setCreateSystemUser] = useState(false)
    const [userPassword, setUserPassword] = useState('')
    const [userRoleId, setUserRoleId] = useState<number | null>(null)
    const [roles, setRoles] = useState<Role[]>([])
    const [companyUsers, setCompanyUsers] = useState<User[]>([])
    const [loadingRoles, setLoadingRoles] = useState(false)
    const [loadingUsers, setLoadingUsers] = useState(false)
    const [userSearch, setUserSearch] = useState('')

    useEffect(() => {
        loadCostCenters()
        loadRoles()
        if (user?.companyId) {
            loadCompanyUsers(user.companyId)
        }
    }, [])

    // Admissão nasce na unidade ativa — trocar a filial é exceção, não rotina.
    useEffect(() => {
        if (isEditing || formData.companyId || !user?.companyId) return
        setFormData(prev => ({ ...prev, companyId: String(user.companyId) }))
    }, [isEditing, user?.companyId, formData.companyId])

    useEffect(() => {
        if (isEditing && id) {
            loadEmployee()
        }
    }, [id, isEditing])

    const loadCostCenters = async () => {
        try {
            const response = await getCostCenters()
            if (response.success && response.result) {
                setCostCenters(
                    Array.isArray(response.result)
                        ? response.result.filter(c => c.active)
                        : []
                )
            }
        } catch {
            // silencioso
        }
    }

    const loadRoles = async () => {
        setLoadingRoles(true)
        try {
            const response = await getRoles()
            if (response.success && response.result) {
                setRoles(Array.isArray(response.result) ? response.result : [])
            }
        } catch {
            // silencioso
        } finally {
            setLoadingRoles(false)
        }
    }

    const loadCompanyUsers = async (companyId: number) => {
        setLoadingUsers(true)
        try {
            const response = await getUsersByCompany(companyId)
            if (response.success && response.result) {
                setCompanyUsers(Array.isArray(response.result) ? response.result : [])
            }
        } catch {
            // silencioso
        } finally {
            setLoadingUsers(false)
        }
    }

    const loadEmployee = async () => {
        if (!id) return

        setLoadingData(true)
        try {
            const response = await getHrEmployee(parseInt(id, 10))
            if (response.success && response.result) {
                const emp = response.result
                setFormData({
                    name: emp.name || '',
                    cpf: emp.cpf ? formatCpf(emp.cpf) : '',
                    rg: emp.rg || '',
                    rgIssuer: (emp as any).rgIssuer || '',
                    rgState: (emp as any).rgState || '',
                    birthDate: emp.birthDate ? emp.birthDate.substring(0, 10) : '',
                    gender: emp.gender || '',
                    maritalStatus: emp.maritalStatus || '',
                    nationality: emp.nationality || 'Brasileira',
                    birthCity: (emp as any).birthCity || '',
                    birthState: (emp as any).birthState || '',
                    motherName: (emp as any).motherName || '',
                    fatherName: (emp as any).fatherName || '',
                    race: emp.race || '',
                    pis: emp.pis || '',
                    ctpsNumber: (emp as any).ctpsNumber || '',
                    ctpsSeries: (emp as any).ctpsSeries || '',
                    ctpsState: (emp as any).ctpsState || '',
                    ctpsIssueDate: (emp as any).ctpsIssueDate
                        ? (emp as any).ctpsIssueDate.substring(0, 10)
                        : '',
                    voterTitle: (emp as any).voterTitle || '',
                    militaryCertificate: (emp as any).militaryCertificate || '',
                    email: emp.email || '',
                    phone: emp.phone ? formatPhone(emp.phone) : '',
                    phone2: (emp as any).phone2 ? formatPhone((emp as any).phone2) : '',
                    whatsAppNumber: emp.whatsAppNumber ? formatPhone(emp.whatsAppNumber) : '',
                    street: emp.street || '',
                    number: (emp as any).number || '',
                    complement: (emp as any).complement || '',
                    neighborhood: (emp as any).neighborhood || '',
                    city: emp.city || '',
                    state: emp.state || '',
                    zipCode: emp.zipCode ? formatZipCode(emp.zipCode) : '',
                    bankCode: (emp as any).bankCode || '',
                    bankName: emp.bankName || '',
                    bankAgency: emp.bankAgency || '',
                    bankAccount: emp.bankAccount || '',
                    bankAccountDigit: emp.bankAccountDigit || '',
                    bankAccountType: (emp as any).bankAccountType || '',
                    pixKey: (emp as any).pixKey || '',
                    pixKeyType: (emp as any).pixKeyType || '',
                    companyId: emp.companyId ? String(emp.companyId) : '',
                    admissionDate: emp.admissionDate
                        ? emp.admissionDate.substring(0, 10)
                        : '',
                    contractType: emp.contractType || 'CLT',
                    workRegime: emp.workRegime || 'ONSITE',
                    weeklyHours: emp.weeklyHours != null ? String(emp.weeklyHours) : '44',
                    jobTitle: emp.jobTitle || '',
                    jobFunction: (emp as any).jobFunction || '',
                    cboCode: emp.cboCode || '',
                    department: emp.department || '',
                    costCenterId: emp.costCenterId != null ? String(emp.costCenterId) : '',
                    salaryReais:
                        emp.salary != null
                            ? formatCurrency(String(emp.salary))
                            : '',
                    salaryType: emp.salaryType || 'MONTHLY',
                    transportVoucher: (emp as any).transportVoucher ?? false,
                    experienceDays: (emp as any).experienceDays != null
                        ? String((emp as any).experienceDays)
                        : '90',
                    userId: emp.userId != null ? String(emp.userId) : '',
                })
            } else {
                toast.error('Funcionário não encontrado')
                navigate('/hr/funcionarios')
            }
        } catch {
            toast.error('Erro ao carregar funcionário')
            navigate('/hr/funcionarios')
        } finally {
            setLoadingData(false)
        }
    }

    const handleChange = (field: string, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }))
    }

    const handleMaskedChange = (field: string, value: string, formatter: (v: string) => string) => {
        setFormData(prev => ({ ...prev, [field]: formatter(value) }))
    }

    const handleZipCodeChange = useCallback(async (value: string) => {
        const formatted = formatZipCode(value)
        setFormData(prev => ({ ...prev, zipCode: formatted }))

        const digits = value.replace(/\D/g, '')
        if (digits.length === 8) {
            setLoadingCep(true)
            try {
                const result = await fetchPostalCode(digits)
                setFormData(prev => ({
                    ...prev,
                    street: result.logradouro || prev.street,
                    neighborhood: result.bairro || prev.neighborhood,
                    city: result.localidade || prev.city,
                    state: result.uf || prev.state,
                }))
            } catch (err) {
                if (err instanceof PostalCodeException) {
                    toast.warning(err.message)
                }
            } finally {
                setLoadingCep(false)
            }
        }
    }, [])

    const handleSalaryChange = (value: string) => {
        handleMaskedChange('salaryReais', value, formatCurrency)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!user?.companyId) {
            toast.error('Usuário não autenticado')
            return
        }

        if (!formData.name.trim()) {
            toast.warning('Preencha o nome do funcionário')
            setActiveTab('personal')
            return
        }

        if (createSystemUser && !userPassword.trim()) {
            toast.warning('Preencha a senha para o novo usuário')
            setActiveTab('contract')
            return
        }

        setLoading(true)

        try {
            const payload: any = {
                name: formData.name.trim(),
                cpf: formData.cpf.replace(/\D/g, '') || null,
                rg: formData.rg || null,
                rgIssuer: formData.rgIssuer || null,
                rgState: formData.rgState || null,
                birthDate: formData.birthDate || null,
                gender: formData.gender || null,
                maritalStatus: formData.maritalStatus || null,
                nationality: formData.nationality || null,
                birthCity: formData.birthCity || null,
                birthState: formData.birthState || null,
                motherName: formData.motherName || null,
                fatherName: formData.fatherName || null,
                race: formData.race || null,
                pis: formData.pis || null,
                ctpsNumber: formData.ctpsNumber || null,
                ctpsSeries: formData.ctpsSeries || null,
                ctpsState: formData.ctpsState || null,
                ctpsIssueDate: formData.ctpsIssueDate || null,
                voterTitle: formData.voterTitle || null,
                militaryCertificate: formData.militaryCertificate || null,
                email: formData.email || null,
                phone: formData.phone.replace(/\D/g, '') || null,
                phone2: formData.phone2.replace(/\D/g, '') || null,
                whatsAppNumber: formData.whatsAppNumber.replace(/\D/g, '') || null,
                street: formData.street || null,
                number: formData.number || null,
                complement: formData.complement || null,
                neighborhood: formData.neighborhood || null,
                city: formData.city || null,
                state: formData.state || null,
                zipCode: formData.zipCode.replace(/\D/g, '') || null,
                bankCode: formData.bankCode || null,
                bankName: formData.bankName || null,
                bankAgency: formData.bankAgency || null,
                bankAccount: formData.bankAccount || null,
                bankAccountDigit: formData.bankAccountDigit || null,
                bankAccountType: formData.bankAccountType || null,
                pixKey: formData.pixKey || null,
                pixKeyType: formData.pixKeyType || null,
                // Só na admissão: trocar a filial de quem já está contratado é
                // transferência de vínculo, não edição de cadastro.
                ...(isEditing || !formData.companyId
                    ? {}
                    : { companyId: parseInt(formData.companyId, 10) }),
                admissionDate: formData.admissionDate || null,
                contractType: formData.contractType || 'CLT',
                workRegime: formData.workRegime || 'ONSITE',
                weeklyHours: formData.weeklyHours ? parseInt(formData.weeklyHours, 10) : null,
                jobTitle: formData.jobTitle || null,
                jobFunction: formData.jobFunction || null,
                cboCode: formData.cboCode || null,
                department: formData.department || null,
                costCenterId: formData.costCenterId
                    ? parseInt(formData.costCenterId, 10)
                    : null,
                salary: formData.salaryReais ? parseCurrencyToInt(formData.salaryReais) : null,
                salaryType: formData.salaryType || 'MONTHLY',
                transportVoucher: formData.transportVoucher,
                experienceDays: formData.experienceDays
                    ? parseInt(formData.experienceDays, 10)
                    : null,
            }

            if (createSystemUser) {
                payload.createSystemUser = true
                payload.userPassword = userPassword
                payload.userRoleId = userRoleId
            } else {
                payload.userId = formData.userId
                    ? parseInt(formData.userId, 10)
                    : null
            }

            let response
            if (isEditing) {
                response = await updateHrEmployee(parseInt(id!, 10), payload)
            } else {
                response = await createHrEmployee(payload)
            }

            if (response.success) {
                toast.success(
                    isEditing
                        ? 'Funcionário atualizado com sucesso'
                        : 'Funcionário cadastrado com sucesso'
                )
                navigate('/hr/funcionarios')
            } else {
                toast.error(
                    response.error ||
                        (isEditing
                            ? 'Erro ao atualizar funcionário'
                            : 'Erro ao cadastrar funcionário')
                )
            }
        } catch {
            toast.error(
                isEditing
                    ? 'Erro ao atualizar funcionário'
                    : 'Erro ao cadastrar funcionário'
            )
        } finally {
            setLoading(false)
        }
    }

    const filteredUsers = companyUsers.filter(u => {
        if (!userSearch.trim()) return true
        const search = userSearch.toLowerCase()
        return (
            u.name?.toLowerCase().includes(search) ||
            u.email?.toLowerCase().includes(search)
        )
    })

    if (loadingData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando funcionário...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/hr/funcionarios')}
                    className="btn btn-ghost btn-circle btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-neutral to-neutral/70 flex items-center justify-center shadow-lg">
                        <MdBadge className="text-2xl text-white" />
                    </div>
                    <h1 className="text-2xl font-bold text-base-content">
                        {isEditing ? 'Editar Funcionário' : 'Novo Funcionário'}
                    </h1>
                </div>
            </div>

            {/* Tabs */}
            <div role="tablist" className="tabs tabs-bordered">
                {TABS.map(tab => (
                    <button
                        key={tab.key}
                        role="tab"
                        className={`tab ${activeTab === tab.key ? 'tab-active' : ''}`}
                        onClick={() => setActiveTab(tab.key)}
                        type="button"
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                {/* Tab: Dados Pessoais */}
                {activeTab === 'personal' && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-4">
                            Dados Pessoais
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div className="form-control w-full md:col-span-2 lg:col-span-3">
                                <label className="label">
                                    <span className="label-text">
                                        Nome completo <span className="text-error">*</span>
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Nome completo do funcionário"
                                    className="input input-bordered w-full"
                                    value={formData.name}
                                    onChange={e => handleChange('name', e.target.value)}
                                    required
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">CPF</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="000.000.000-00"
                                    className="input input-bordered w-full"
                                    value={formData.cpf}
                                    onChange={e => handleMaskedChange('cpf', e.target.value, formatCpf)}
                                    maxLength={14}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">RG</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Número do RG"
                                    className="input input-bordered w-full"
                                    value={formData.rg}
                                    onChange={e => handleChange('rg', e.target.value)}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Órgão emissor</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="SSP, DETRAN, etc."
                                    className="input input-bordered w-full"
                                    value={formData.rgIssuer}
                                    onChange={e => handleChange('rgIssuer', e.target.value)}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">UF do RG</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="UF"
                                    className="input input-bordered w-full"
                                    value={formData.rgState}
                                    onChange={e => handleChange('rgState', e.target.value)}
                                    maxLength={2}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Data de nascimento</span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={formData.birthDate}
                                    onChange={e => handleChange('birthDate', e.target.value)}
                                    onPaste={e =>
                                        handleDateInputPaste(e, isoDate =>
                                            handleChange('birthDate', isoDate),
                                        )
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Gênero</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.gender}
                                    onChange={e => handleChange('gender', e.target.value)}
                                >
                                    <option value="">Selecione</option>
                                    {Object.entries(GENDER_LABELS).map(([k, v]) => (
                                        <option key={k} value={k}>{v}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Estado civil</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.maritalStatus}
                                    onChange={e => handleChange('maritalStatus', e.target.value)}
                                >
                                    <option value="">Selecione</option>
                                    {Object.entries(MARITAL_STATUS_LABELS).map(([k, v]) => (
                                        <option key={k} value={k}>{v}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Nacionalidade</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Brasileira"
                                    className="input input-bordered w-full"
                                    value={formData.nationality}
                                    onChange={e => handleChange('nationality', e.target.value)}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Cidade natal</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Cidade de nascimento"
                                    className="input input-bordered w-full"
                                    value={formData.birthCity}
                                    onChange={e => handleChange('birthCity', e.target.value)}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Estado natal</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="UF"
                                    className="input input-bordered w-full"
                                    value={formData.birthState}
                                    onChange={e => handleChange('birthState', e.target.value)}
                                    maxLength={2}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Nome da mãe</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Nome completo da mãe"
                                    className="input input-bordered w-full"
                                    value={formData.motherName}
                                    onChange={e => handleChange('motherName', e.target.value)}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Nome do pai</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Nome completo do pai"
                                    className="input input-bordered w-full"
                                    value={formData.fatherName}
                                    onChange={e => handleChange('fatherName', e.target.value)}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Raça/Cor</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.race}
                                    onChange={e => handleChange('race', e.target.value)}
                                >
                                    <option value="">Selecione</option>
                                    {Object.entries(RACE_LABELS).map(([k, v]) => (
                                        <option key={k} value={k}>{v}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                )}

                {/* Tab: Documentos */}
                {activeTab === 'documents' && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-4">
                            Documentos
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">PIS/PASEP</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Número do PIS"
                                    className="input input-bordered w-full"
                                    value={formData.pis}
                                    onChange={e => handleChange('pis', e.target.value)}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">CTPS - Número</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Número da CTPS"
                                    className="input input-bordered w-full"
                                    value={formData.ctpsNumber}
                                    onChange={e => handleChange('ctpsNumber', e.target.value)}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">CTPS - Série</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Série da CTPS"
                                    className="input input-bordered w-full"
                                    value={formData.ctpsSeries}
                                    onChange={e => handleChange('ctpsSeries', e.target.value)}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">CTPS - UF</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="UF da CTPS"
                                    className="input input-bordered w-full"
                                    value={formData.ctpsState}
                                    onChange={e => handleChange('ctpsState', e.target.value)}
                                    maxLength={2}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">CTPS - Data de emissão</span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={formData.ctpsIssueDate}
                                    onChange={e => handleChange('ctpsIssueDate', e.target.value)}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Título de eleitor</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Número do título"
                                    className="input input-bordered w-full"
                                    value={formData.voterTitle}
                                    onChange={e => handleChange('voterTitle', e.target.value)}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Certificado militar</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Número do certificado"
                                    className="input input-bordered w-full"
                                    value={formData.militaryCertificate}
                                    onChange={e => handleChange('militaryCertificate', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Tab: Contato */}
                {activeTab === 'contact' && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-4">
                            Contato
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">E-mail</span>
                                </label>
                                <input
                                    type="email"
                                    placeholder="email@exemplo.com"
                                    className="input input-bordered w-full"
                                    value={formData.email}
                                    onChange={e => handleChange('email', e.target.value)}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Telefone</span>
                                </label>
                                <input
                                    type="tel"
                                    placeholder="(00) 00000-0000"
                                    className="input input-bordered w-full"
                                    value={formData.phone}
                                    onChange={e => handleMaskedChange('phone', e.target.value, formatPhone)}
                                    maxLength={15}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Telefone 2</span>
                                </label>
                                <input
                                    type="tel"
                                    placeholder="(00) 00000-0000"
                                    className="input input-bordered w-full"
                                    value={formData.phone2}
                                    onChange={e => handleMaskedChange('phone2', e.target.value, formatPhone)}
                                    maxLength={15}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">WhatsApp</span>
                                </label>
                                <input
                                    type="tel"
                                    placeholder="(00) 00000-0000"
                                    className="input input-bordered w-full"
                                    value={formData.whatsAppNumber}
                                    onChange={e => handleMaskedChange('whatsAppNumber', e.target.value, formatPhone)}
                                    maxLength={15}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Tab: Endereço */}
                {activeTab === 'address' && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-4">
                            Endereço
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">CEP</span>
                                </label>
                                <label className="input input-bordered flex items-center gap-2 w-full">
                                    <input
                                        type="text"
                                        placeholder="00000-000"
                                        className="grow"
                                        value={formData.zipCode}
                                        onChange={e => handleZipCodeChange(e.target.value)}
                                        maxLength={9}
                                    />
                                    {loadingCep && (
                                        <span className="loading loading-spinner loading-xs"></span>
                                    )}
                                </label>
                            </div>

                            <div className="form-control w-full lg:col-span-2">
                                <label className="label">
                                    <span className="label-text">Logradouro</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Rua, Avenida, etc."
                                    className="input input-bordered w-full"
                                    value={formData.street}
                                    onChange={e => handleChange('street', e.target.value)}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Número</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Nº"
                                    className="input input-bordered w-full"
                                    value={formData.number}
                                    onChange={e => handleChange('number', e.target.value)}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Complemento</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Apto, Bloco, etc."
                                    className="input input-bordered w-full"
                                    value={formData.complement}
                                    onChange={e => handleChange('complement', e.target.value)}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Bairro</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Bairro"
                                    className="input input-bordered w-full"
                                    value={formData.neighborhood}
                                    onChange={e => handleChange('neighborhood', e.target.value)}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Cidade</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Cidade"
                                    className="input input-bordered w-full"
                                    value={formData.city}
                                    onChange={e => handleChange('city', e.target.value)}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Estado</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="UF"
                                    className="input input-bordered w-full"
                                    value={formData.state}
                                    onChange={e => handleChange('state', e.target.value)}
                                    maxLength={2}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Tab: Dados Bancários */}
                {activeTab === 'bank' && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-4">
                            Dados Bancários
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Código do banco</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="001, 237, 341..."
                                    className="input input-bordered w-full"
                                    value={formData.bankCode}
                                    onChange={e => handleChange('bankCode', e.target.value)}
                                    maxLength={4}
                                />
                            </div>

                            <div className="form-control w-full lg:col-span-2">
                                <label className="label">
                                    <span className="label-text">Nome do banco</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Nome do banco"
                                    className="input input-bordered w-full"
                                    value={formData.bankName}
                                    onChange={e => handleChange('bankName', e.target.value)}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Agência</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Número da agência"
                                    className="input input-bordered w-full"
                                    value={formData.bankAgency}
                                    onChange={e => handleChange('bankAgency', e.target.value)}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Conta</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Número da conta"
                                    className="input input-bordered w-full"
                                    value={formData.bankAccount}
                                    onChange={e => handleChange('bankAccount', e.target.value)}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Dígito da conta</span>
                                </label>
                                <input
                                    type="text"
                                    maxLength={2}
                                    placeholder="Ex: 6"
                                    className="input input-bordered w-full"
                                    value={formData.bankAccountDigit}
                                    onChange={e =>
                                        handleChange('bankAccountDigit', e.target.value)
                                    }
                                />
                                <label className="label py-1">
                                    <span className="label-text-alt text-base-content/50">
                                        Obrigatório para pagamento por TED
                                    </span>
                                </label>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Tipo de conta</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.bankAccountType}
                                    onChange={e => handleChange('bankAccountType', e.target.value)}
                                >
                                    <option value="">Selecione</option>
                                    {Object.entries(BANK_ACCOUNT_TYPE_LABELS).map(([k, v]) => (
                                        <option key={k} value={k}>{v}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Tipo de chave PIX</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.pixKeyType}
                                    onChange={e => handleChange('pixKeyType', e.target.value)}
                                >
                                    <option value="">Selecione</option>
                                    {Object.entries(PIX_KEY_TYPE_LABELS).map(([k, v]) => (
                                        <option key={k} value={k}>{v}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-control w-full lg:col-span-2">
                                <label className="label">
                                    <span className="label-text">Chave PIX</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Chave PIX"
                                    className="input input-bordered w-full"
                                    value={formData.pixKey}
                                    onChange={e => handleChange('pixKey', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Tab: Contrato */}
                {activeTab === 'contract' && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-4">
                            Dados do Contrato
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {/* O cadastro é da organização, mas o vínculo é com UMA
                                filial: é o CNPJ dela que assina a carteira, declara o
                                eSocial e paga a folha. Por isso a filial fica aqui, no
                                contrato, e não nos dados pessoais. */}
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">
                                        Filial empregadora
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.companyId}
                                    onChange={e => handleChange('companyId', e.target.value)}
                                    disabled={isEditing}
                                >
                                    {companyOptions.map(company => (
                                        <option key={company.id} value={String(company.id)}>
                                            {company.name}
                                        </option>
                                    ))}
                                </select>
                                <label className="label">
                                    <span className="label-text-alt text-base-content/60">
                                        {isEditing
                                            ? 'Trocar de filial é transferência — refaça pelo desligamento e nova admissão.'
                                            : 'Quem assina a carteira e paga a folha deste funcionário.'}
                                    </span>
                                </label>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Data de admissão</span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={formData.admissionDate}
                                    onChange={e => handleChange('admissionDate', e.target.value)}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Tipo de contrato</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.contractType}
                                    onChange={e => handleChange('contractType', e.target.value)}
                                >
                                    {Object.entries(CONTRACT_TYPE_LABELS).map(([k, v]) => (
                                        <option key={k} value={k}>{v}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Regime de trabalho</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.workRegime}
                                    onChange={e => handleChange('workRegime', e.target.value)}
                                >
                                    {Object.entries(WORK_REGIME_LABELS).map(([k, v]) => (
                                        <option key={k} value={k}>{v}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Carga horária semanal</span>
                                </label>
                                <input
                                    type="number"
                                    placeholder="44"
                                    className="input input-bordered w-full"
                                    value={formData.weeklyHours}
                                    onChange={e => handleChange('weeklyHours', e.target.value)}
                                    min={0}
                                    max={168}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Cargo</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Cargo do funcionário"
                                    className="input input-bordered w-full"
                                    value={formData.jobTitle}
                                    onChange={e => handleChange('jobTitle', e.target.value)}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Função</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Função exercida"
                                    className="input input-bordered w-full"
                                    value={formData.jobFunction}
                                    onChange={e => handleChange('jobFunction', e.target.value)}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">CBO</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Código CBO"
                                    className="input input-bordered w-full"
                                    value={formData.cboCode}
                                    onChange={e => handleChange('cboCode', e.target.value)}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Departamento</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Departamento"
                                    className="input input-bordered w-full"
                                    value={formData.department}
                                    onChange={e => handleChange('department', e.target.value)}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Centro de custo</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.costCenterId}
                                    onChange={e => handleChange('costCenterId', e.target.value)}
                                >
                                    <option value="">Nenhum</option>
                                    {costCenters.map(cc => (
                                        <option key={cc.id} value={String(cc.id)}>
                                            {cc.code ? `${cc.code} - ${cc.name}` : cc.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Salário (R$)</span>
                                </label>
                                <label className="input input-bordered flex items-center gap-2 w-full">
                                    <span className="text-base-content/50 text-sm">R$</span>
                                    <input
                                        type="text"
                                        placeholder="0,00"
                                        className="grow"
                                        value={formData.salaryReais}
                                        onChange={e => handleSalaryChange(e.target.value)}
                                    />
                                </label>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Tipo de salário</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.salaryType}
                                    onChange={e => handleChange('salaryType', e.target.value)}
                                >
                                    {Object.entries(SALARY_TYPE_LABELS).map(([k, v]) => (
                                        <option key={k} value={k}>{v}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Dias de experiência</span>
                                </label>
                                <input
                                    type="number"
                                    placeholder="90"
                                    className="input input-bordered w-full"
                                    value={formData.experienceDays}
                                    onChange={e => handleChange('experienceDays', e.target.value)}
                                    min={0}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label cursor-pointer justify-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-secondary"
                                        checked={formData.transportVoucher as boolean}
                                        onChange={e => setFormData(prev => ({ ...prev, transportVoucher: e.target.checked }))}
                                    />
                                    <span className="label-text">Recebe Vale-Transporte</span>
                                </label>
                            </div>
                        </div>

                        {/* Acesso ao Sistema */}
                        <div className="divider mt-6 mb-4">Acesso ao Sistema</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div className="form-control w-full md:col-span-2 lg:col-span-3">
                                <label className="label cursor-pointer justify-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-secondary"
                                        checked={createSystemUser}
                                        onChange={e => {
                                            setCreateSystemUser(e.target.checked)
                                            if (e.target.checked) {
                                                handleChange('userId', '')
                                            } else {
                                                setUserPassword('')
                                                setUserRoleId(null)
                                            }
                                        }}
                                    />
                                    <span className="label-text font-medium">
                                        Criar acesso ao sistema
                                    </span>
                                </label>
                            </div>

                            {createSystemUser ? (
                                <>
                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text">
                                                Senha <span className="text-error">*</span>
                                            </span>
                                        </label>
                                        <input
                                            type="password"
                                            placeholder="Senha do novo usuário"
                                            className="input input-bordered w-full"
                                            value={userPassword}
                                            onChange={e => setUserPassword(e.target.value)}
                                            required={createSystemUser}
                                        />
                                    </div>

                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text">Perfil de acesso</span>
                                        </label>
                                        <select
                                            className="select select-bordered w-full"
                                            value={userRoleId ?? ''}
                                            onChange={e => setUserRoleId(e.target.value ? parseInt(e.target.value, 10) : null)}
                                            disabled={loadingRoles}
                                        >
                                            <option value="">Selecione um perfil</option>
                                            {roles.map(role => (
                                                <option key={role.id} value={role.id}>
                                                    {role.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </>
                            ) : (
                                <div className="form-control w-full md:col-span-2">
                                    <label className="label">
                                        <span className="label-text">
                                            Vincular a um usuário existente{' '}
                                            <span className="text-base-content/40 text-xs">(opcional)</span>
                                        </span>
                                    </label>
                                    <div className="dropdown w-full">
                                        <label className="input input-bordered flex items-center gap-2 w-full">
                                            <input
                                                type="text"
                                                placeholder={loadingUsers ? 'Carregando usuários...' : 'Buscar usuário por nome ou e-mail'}
                                                className="grow"
                                                value={
                                                    formData.userId
                                                        ? companyUsers.find(u => u.id === parseInt(formData.userId, 10))?.name || userSearch
                                                        : userSearch
                                                }
                                                onChange={e => {
                                                    setUserSearch(e.target.value)
                                                    if (formData.userId) {
                                                        handleChange('userId', '')
                                                    }
                                                }}
                                                onFocus={e => {
                                                    (e.target.closest('.dropdown') as HTMLElement)?.classList.add('dropdown-open')
                                                }}
                                                disabled={loadingUsers}
                                            />
                                            {formData.userId && (
                                                <button
                                                    type="button"
                                                    className="btn btn-ghost btn-xs btn-circle"
                                                    onClick={() => {
                                                        handleChange('userId', '')
                                                        setUserSearch('')
                                                    }}
                                                >
                                                    x
                                                </button>
                                            )}
                                            {loadingUsers && (
                                                <span className="loading loading-spinner loading-xs"></span>
                                            )}
                                        </label>
                                        {!formData.userId && (
                                            <ul className="dropdown-content z-[1] menu p-2 shadow-lg bg-base-100 rounded-box w-full max-h-60 overflow-y-auto border border-base-300">
                                                {filteredUsers.length === 0 ? (
                                                    <li className="disabled">
                                                        <span className="text-base-content/50">
                                                            Nenhum usuário encontrado
                                                        </span>
                                                    </li>
                                                ) : (
                                                    filteredUsers.slice(0, 50).map(u => (
                                                        <li key={u.id}>
                                                            <button
                                                                type="button"
                                                                className="flex flex-col items-start"
                                                                onClick={() => {
                                                                    handleChange('userId', String(u.id))
                                                                    setUserSearch('')
                                                                    const dropdown = document.activeElement?.closest('.dropdown')
                                                                    if (dropdown) {
                                                                        dropdown.classList.remove('dropdown-open')
                                                                        ;(document.activeElement as HTMLElement)?.blur()
                                                                    }
                                                                }}
                                                            >
                                                                <span className="font-medium">{u.name}</span>
                                                                <span className="text-xs text-base-content/50">{u.email}</span>
                                                            </button>
                                                        </li>
                                                    ))
                                                )}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Submit */}
                <div className="flex gap-2 pt-2">
                    <button
                        type="button"
                        className="btn btn-ghost flex-1"
                        onClick={() => navigate('/hr/funcionarios')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary flex-1 gap-2"
                        disabled={loading || !formData.name.trim()}
                    >
                        {loading ? (
                            <span className="loading loading-spinner"></span>
                        ) : (
                            <>
                                <MdSave className="text-xl" />
                                {isEditing ? 'Atualizar' : 'Salvar'}
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    )
}
